// Meta (Facebook) Pixel conversion tracking helper.
//
// The pixel is initialized in components/CookieConsent.js, but ONLY after the visitor accepts
// analytics/marketing cookies — so window.fbq may not exist. Every call here is guarded and
// wrapped in try/catch so tracking can never break a user flow (apply, signup, page load).
//
// Sign-ups and applications are ALSO reported from our own server (Meta Conversions API,
// backend /api/meta/registration + the apply endpoints) because the browser pixel is blind to
// anyone who declined cookies, uses an in-app browser, or blocks trackers. Both sides tag the
// same action with ONE shared event id (reg-<auth user id>, app-<application id>) so Meta
// dedups the pair instead of counting it twice.
//
// Standard events used in this app:
//   - PageView            (CookieConsent.js, on load)
//   - CompleteRegistration (Auth.js on email/phone signup, AuthCallback.js after verification,
//                           ProfileCompletion.js when a Google/Facebook user finishes setup)
//   - ViewContent          (JobDetails.js, when a job page is viewed)
//   - SubmitApplication    (JobDetails.js, when a job application is submitted)
// Custom event:
//   - ApplyIntent          (Jobs.js / JobDetails.js, a logged-out visitor tapped Apply / Submit Bid)
import api from './api';
import { getAttribution, getStoredFbc } from './attribution';

const CONSENT_KEY = 'analytics_consent';
const FRESH_ACCOUNT_HOURS = 48; // mirrors META_REGISTRATION_MAX_AGE_HOURS on the backend

export function trackPixel(event, params, eventId) {
  try {
    if (typeof window !== 'undefined' && window.fbq) {
      if (eventId) {
        window.fbq('track', event, params, { eventID: eventId });
      } else {
        window.fbq('track', event, params);
      }
    }
  } catch (e) {
    /* never let tracking break a user flow */
  }
}

export function hasAnalyticsConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY) === 'accepted';
  } catch (e) {
    return false;
  }
}

// True only when the visitor EXPLICITLY tapped Decline (distinct from "not chosen yet").
export function analyticsDeclined() {
  try {
    return localStorage.getItem(CONSENT_KEY) === 'declined';
  } catch (e) {
    return false;
  }
}

// A stable per-browser id so repeated views of the same job by the same visitor share one
// event id and dedup within Meta's window (instead of inflating the view count on refresh).
function getVisitorId() {
  try {
    let v = localStorage.getItem('bt_visitor_id');
    if (!v) {
      v = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      localStorage.setItem('bt_visitor_id', v);
    }
    return v;
  } catch (e) {
    return undefined;
  }
}

function readCookie(name) {
  try {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : undefined;
  } catch (e) {
    return undefined;
  }
}

// Meta click ids for a server report, honouring the cookie choice:
//   declined  -> nothing (no fbp, no click id)
//   accepted  -> the pixel's _fbp/_fbc cookies (click id falls back to the one we captured)
//   undecided -> only the click id we captured from the ad link (no cookies exist yet)
export function metaIds() {
  if (analyticsDeclined()) return { consent: false };
  const consent = hasAnalyticsConsent();
  return {
    consent,
    fbp: consent ? readCookie('_fbp') : undefined,
    click_id: (consent ? readCookie('_fbc') : undefined) || getStoredFbc(),
  };
}

// Extra fields for the apply endpoints: where the visitor came from (our own record, sent
// regardless of cookie choice) + the Meta click ids above. Never throws.
export function applyTrackingFields() {
  try {
    return { attribution: getAttribution(), ...metaIds() };
  } catch (e) {
    return {};
  }
}

// True when the account is new enough to still count as a sign-up (or when we can't tell).
export function isFreshAccount(createdAt, hours = FRESH_ACCOUNT_HOURS) {
  if (!createdAt) return true;
  const ts = new Date(createdAt).getTime();
  if (Number.isNaN(ts)) return true;
  return Date.now() - ts < hours * 60 * 60 * 1000;
}

// Report a completed sign-up: server first (Conversions API — works even when the pixel was
// declined or blocked), then the browser pixel with the SAME event id. Best-effort and
// bounded (5s) so it can never hold up or break the redirect that follows it.
export async function reportRegistration({ token, userId, role, createdAt, pixel = true } = {}) {
  if (!isFreshAccount(createdAt)) return;
  let eventId = userId ? `reg-${userId}` : undefined;
  if (token) {
    try {
      const consent = hasAnalyticsConsent();
      const ids = metaIds();
      const res = await api.post(
        '/api/meta/registration',
        {
          consent,
          fbp: consent ? readCookie('_fbp') : undefined,
          fbc: consent ? readCookie('_fbc') : undefined,
          click_id: ids.click_id,
          attribution: getAttribution(), // stamped once on the new account (first touch)
          source_url: typeof window !== 'undefined' ? window.location.href : undefined,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 }
      );
      if (res?.data?.event_id) eventId = res.data.event_id;
    } catch (e) {
      /* server mirror is best-effort; the browser pixel below still fires */
    }
  }
  if (pixel) {
    trackPixel('CompleteRegistration', { content_name: role || 'user' }, eventId);
  }
}

// Report a job view for ad measurement. Fires server-side (Conversions API) so the view still
// counts for visitors the browser pixel is blind to — hasn't chosen cookies yet, in-app
// browser, or tracker-blocked — then fires the browser pixel with the SAME event id so Meta
// dedups the pair. Respects an explicit Decline: if the visitor tapped Decline we report
// NOTHING (server or browser). Best-effort and bounded (5s) so it never blocks the page.
export async function reportJobView({ jobId, jobTitle } = {}) {
  if (!jobId) return;
  if (analyticsDeclined()) return; // Decline is fully respected — no server or browser report
  const visitor = getVisitorId();
  const eventId = `view-${jobId}-${visitor || Date.now()}`;
  const consent = hasAnalyticsConsent();
  try {
    await api.post(
      '/api/meta/view-content',
      {
        job_id: jobId,
        event_id: eventId,
        consent,
        fbp: consent ? readCookie('_fbp') : undefined,
        fbc: consent ? readCookie('_fbc') : undefined,
        source_url: typeof window !== 'undefined' ? window.location.href : undefined,
      },
      { timeout: 5000 }
    );
  } catch (e) {
    /* server mirror is best-effort; the browser pixel below still fires when loaded */
  }
  // No-ops until the pixel is loaded (i.e. the visitor accepted) — undecided visitors are
  // covered by the server call above, declined visitors returned early.
  trackPixel('ViewContent', { content_type: 'job', content_ids: [jobId], content_name: jobTitle }, eventId);
}

// Per-tab id for visitors who DECLINED analytics: lets our own count dedupe repeat taps in
// one visit without keeping any lasting identifier (sessionStorage dies with the tab).
function getTabId() {
  try {
    let v = sessionStorage.getItem('bt_tab_id');
    if (!v) {
      v = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      sessionStorage.setItem('bt_tab_id', v);
    }
    return v;
  } catch (e) {
    return undefined;
  }
}

// A logged-out visitor tapped Apply / Submit Bid and is being sent to sign up — the step
// between "viewed a job" and "signed up". Always counted in OUR database (per source, admin
// Marketing tab) — the cookie bar's ✕ is Decline, so skipping decliners would lose most
// job-page taps. Only visitors who have NOT declined are also reported to Meta (custom event
// ApplyIntent, mirrored by the browser pixel with the SAME event id); after a Decline the
// server is told `declined` and sends nothing to Meta, and only a per-tab id is used.
// Fire-and-forget: the caller navigates to sign-up straight away.
export function reportApplyIntent({ jobId, postType } = {}) {
  try {
    if (!jobId) return;
    const declined = analyticsDeclined();
    const visitor = declined ? getTabId() : getVisitorId();
    if (!visitor) return;
    const eventId = `intent-${jobId}-${visitor}`;
    api.post(
      '/api/meta/apply-intent',
      {
        job_id: jobId,
        visitor_id: visitor,
        event_id: eventId,
        declined,
        attribution: getAttribution(),
        ...metaIds(),
        source_url: typeof window !== 'undefined' ? window.location.href : undefined,
      },
      { timeout: 5000 }
    ).catch(() => { /* best-effort */ });
    if (!declined && typeof window !== 'undefined' && window.fbq) {
      window.fbq('trackCustom', 'ApplyIntent',
        { content_type: postType === 'gig' ? 'gig' : 'job', content_ids: [jobId] }, { eventID: eventId });
    }
  } catch (e) {
    /* never let tracking break the Apply tap */
  }
}

// Shared event id for an application, from the apply endpoint's response ({ application: { id } }).
export function applicationEventId(response) {
  const id = response?.data?.application?.id;
  return id ? `app-${id}` : undefined;
}
